import { ExperimentNames } from 'common/experiments/ExperimentDefinitions';
import { QueryParams } from 'common/urls/QueryParams';
import { LandingPageVariants } from 'src/pages/consumer/homepage/utils/consts';
import { useLocation } from 'src/shared/hooks/useLocation';
import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { StringParam, useQueryParam } from 'use-query-params';
import { ImmersiveLandingPage } from './immersiveLandingPages';
import { ExperimentResult, useExperiment } from './useExperiment';
import { useLogExposureOnce } from './useLogExposureOnce';

interface ImmersiveLandingPageOptions {
  /**
   * Whether the caller is in a position to log exposures — it has settled its own
   * preconditions, such as knowing the page isn't about to redirect. Only the
   * component rendering the page should pass true; the chrome call sites must not,
   * or one view would log several exposures.
   */
  canLogExposure?: boolean;
}

/**
 * Rejects a list that omits any {@link ImmersiveLandingPage}, so a page added to the
 * enum can't be silently left out of the order and become unresolvable. Omitting one
 * collapses the parameter to `never`, which reads as "not assignable to type 'never'".
 */
const inResolutionOrder = <const T extends readonly ImmersiveLandingPage[]>(
  pages: ImmersiveLandingPage extends T[number] ? T : never
): readonly ImmersiveLandingPage[] => pages;

/**
 * Resolution order when more than one treatment somehow applies. Only one flag is
 * ever meant to be on, so this is a guard against a misconfiguration rather than a
 * feature: the earliest entry wins and the page stays coherent instead of becoming
 * a hybrid.
 */
const PAGES_IN_RESOLUTION_ORDER = inResolutionOrder([
  ImmersiveLandingPage.DishStories,
  ImmersiveLandingPage.FullBleedVideo,
  ImmersiveLandingPage.HowItWorks,
]);

const onlyOnTargetPage = (experiment: ExperimentResult, isOnTargetPage: boolean): ExperimentResult => ({
  isTreatment: experiment.isTreatment && isOnTargetPage,
  isEligible: experiment.isEligible && isOnTargetPage,
  logExposure: experiment.logExposure,
});

/**
 * The immersive landing page for the current view, and the exposure logging for
 * every experiment behind them. Header, promo banner and footer read this too, so
 * their chrome can't drift from what renders.
 *
 * `useExperiment` answers "which arm is this device in", which holds everywhere on
 * the site. These pages only exist on the landing route at `lp_var=04`, so each
 * experiment is narrowed to that before it can count as treatment or as exposed —
 * which also excludes `/meal-plans`, off the landing route and forcing LP_04 with
 * no `lp_var`.
 *
 * @returns the first page whose treatment applies, so two flags on at once resolve
 *   to one page instead of a hybrid; null when none does.
 */
export const useImmersiveLandingPage = ({
  canLogExposure = false,
}: ImmersiveLandingPageOptions = {}): ImmersiveLandingPage | null => {
  const location = useLocation();
  const [lpQueryVariantName] = useQueryParam(QueryParams.LANDING_PAGE_VARIANT, StringParam);

  const rawDishStories = useExperiment(ExperimentNames.LP_DISH_STORIES);
  const rawFullBleedVideo = useExperiment(ExperimentNames.LP_FULL_BLEED_VIDEO);
  const rawHowItWorks = useExperiment(ExperimentNames.LP_HOW_IT_WORKS);

  const isOnTargetPage =
    locationInRoutes(location, [Routes.CONSUMER_LANDING]) && lpQueryVariantName === LandingPageVariants.LP_04;

  const dishStories = onlyOnTargetPage(rawDishStories, isOnTargetPage);
  const fullBleedVideo = onlyOnTargetPage(rawFullBleedVideo, isOnTargetPage);
  const howItWorks = onlyOnTargetPage(rawHowItWorks, isOnTargetPage);

  useLogExposureOnce(canLogExposure && dishStories.isEligible, dishStories.logExposure);
  useLogExposureOnce(canLogExposure && fullBleedVideo.isEligible, fullBleedVideo.logExposure);
  useLogExposureOnce(canLogExposure && howItWorks.isEligible, howItWorks.logExposure);

  /** Exhaustive by construction: a new page can't be added without wiring it up here. */
  const isTreatmentByPage: Record<ImmersiveLandingPage, boolean> = {
    [ImmersiveLandingPage.DishStories]: dishStories.isTreatment,
    [ImmersiveLandingPage.FullBleedVideo]: fullBleedVideo.isTreatment,
    [ImmersiveLandingPage.HowItWorks]: howItWorks.isTreatment,
  };

  return PAGES_IN_RESOLUTION_ORDER.find((page) => isTreatmentByPage[page]) ?? null;
};

export const useIsImmersiveLandingPage = (): boolean => useImmersiveLandingPage() !== null;
